import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin, adminMe, adminStats, adminList } from "../lib/api";
import { Dumbbell, LogOut, Loader2, Inbox, ShoppingBag, Mail, Users, FileText, MessageSquareQuote, Video, Image as ImageIcon, Settings } from "lucide-react";
import { TestimonialsManager, VideoTestimonialsManager, TransformationsManager, SiteSettingsManager } from "./AdminContent";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("mfc-admin-token")) { setAuthed(false); return; }
    adminMe().then(() => setAuthed(true)).catch(() => { localStorage.removeItem("mfc-admin-token"); setAuthed(false); });
  }, []);

  if (authed === null) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>;
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => { localStorage.removeItem("mfc-admin-token"); setAuthed(false); navigate("/admin"); }} />;
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const res = await adminLogin({ email, password });
      localStorage.setItem("mfc-admin-token", res.token);
      onSuccess();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-secondary border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-accent transition-colors";
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6 dark">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm glass rounded-3xl p-8">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-9 h-9 rounded-full bg-accent flex items-center justify-center"><Dumbbell className="w-5 h-5 text-white" /></span>
          <span className="font-heading font-black text-lg uppercase">Admin</span>
        </div>
        <h1 className="font-heading font-black text-2xl mb-6">Sign in</h1>
        <div className="space-y-4">
          <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" className={inputCls} />
          <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" className={inputCls} />
        </div>
        {err && <p className="text-sm text-red-400 mt-3" data-testid="admin-login-error">{err}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-btn"
          className="w-full mt-6 rounded-full bg-accent text-white py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
}

const INBOX_TABS = [
  { key: "applications", label: "Applications", icon: FileText },
  { key: "contacts", label: "Contacts", icon: Mail },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "subscribers", label: "Subscribers", icon: Users },
];

const CMS_TABS = [
  { key: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { key: "video-testimonials", label: "Video Reviews", icon: Video },
  { key: "transformations", label: "Transformations", icon: ImageIcon },
  { key: "site-settings", label: "Site Settings", icon: Settings },
];

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("applications");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const isInbox = INBOX_TABS.some((t) => t.key === tab);

  useEffect(() => { adminStats().then(setStats).catch(() => {}); }, []);
  useEffect(() => {
    if (!isInbox) return;
    setLoading(true);
    adminList(tab).then((d) => setRows(d)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [tab, isInbox]);

  const statCards = [
    { key: "applications", label: "Applications", icon: FileText },
    { key: "contacts", label: "Contacts", icon: Mail },
    { key: "orders", label: "Orders", icon: ShoppingBag },
    { key: "subscribers", label: "Subscribers", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground dark" data-testid="admin-dashboard">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-accent flex items-center justify-center"><Inbox className="w-4 h-4 text-white" /></span>
            <span className="font-heading font-black uppercase">Manav Fit — Admin</span>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-accent">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((s) => (
            <div key={s.key} className="glass rounded-2xl p-5">
              <s.icon className="w-5 h-5 text-accent mb-3" />
              <p className="font-heading font-black text-3xl">{stats[s.key] ?? "—"}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-2">Inbox</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {INBOX_TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${tab === t.key ? "bg-accent text-white" : "glass hover:text-accent"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-2">Content Manager</p>
          <div className="flex flex-wrap gap-2">
            {CMS_TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${tab === t.key ? "bg-accent text-white" : "glass hover:text-accent"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {isInbox ? (
          <div className="glass rounded-2xl overflow-hidden">
            {loading ? (
              <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>
            ) : rows.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">No records yet.</div>
            ) : (
              <div className="divide-y divide-border" data-testid="admin-records">
                {rows.map((r, i) => (
                  <div key={i} className="p-5 hover:bg-secondary/40 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-heading font-bold">{r.name || r.email || "—"}</p>
                      <span className="text-xs text-muted-foreground">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm text-muted-foreground">
                      {Object.entries(r).filter(([k]) => !["name", "created_at", "id"].includes(k)).map(([k, v]) => (
                        <p key={k}><span className="text-foreground/60 capitalize">{k.replace(/_/g, " ")}:</span> {String(v)}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {tab === "testimonials" && <TestimonialsManager />}
            {tab === "video-testimonials" && <VideoTestimonialsManager />}
            {tab === "transformations" && <TransformationsManager />}
            {tab === "site-settings" && <SiteSettingsManager />}
          </div>
        )}
      </main>
    </div>
  );
}
